import { PetCareExperienceOptions } from './PetCareProfileTypes'; // Ensure PetCareExperience is an enum or object
export interface SelectOption {
  value: string;
  display: string;
}

export interface CheckboxOption {
  value: string;
  label: string;
}

export const EDUCATION_LEVELS = [
  'Some High School',
  'High School Degree',
  'Some College',
  'College Degree',
  'Some Graduate School',
  'Graduate Degree',
];

export interface EducationFormState {
  levelOfEducation: string;
  schoolName: string;
  currentlyAttending: boolean;
  major: string;
  yearGraduated: number | null;
}

export const educationLevelOptions = (): SelectOption[] =>
  EDUCATION_LEVELS.map((level) => ({
    value: level.replace(/\s+/g, '_').toUpperCase(),
    display: level,
  }));

export const graduationYearOptions = (): SelectOption[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 60 }, (_, index) => {
    const year = String(currentYear - index);
    return { value: year, display: year };
  });
};

export const distanceOptions = (): SelectOption[] =>
  [1, 2, 5, 10, 15, 25, 40, 50].map((miles) => ({
    value: String(miles),
    display: `${miles} miles`,
  }));

// ---------------------------------------------------------------------------
// Detailed availability windows shared by Companion Care + Housekeeping detailed
// schedules.
// ---------------------------------------------------------------------------
export const DETAILED_AVAILABILITY_OPTIONS: CheckboxOption[] = [
  { value: 'WEEKDAY_MORNINGS', label: 'Weekday Early Morning' },
  { value: 'WEEKDAY_DAYS', label: 'Day-time Weekdays (M - F)' },
  { value: 'WEEKDAY_AFTERNOONS', label: 'Weekday Afternoons' },
  { value: 'WEEKDAY_EVENINGS', label: 'Weekday Evenings' },
  { value: 'WEEKEND_DAYS', label: 'Weekends Daytime' },
  { value: 'WEEKEND_EVENINGS', label: 'Weekends Evenings' },
];

export const TYPICAL_AVAILABILITY_OPTIONS: CheckboxOption[] = [
  { value: 'OPEN', label: 'Open (all day, everyday)' },
  { value: 'WEEKDAYS', label: 'Open Availability on Weekdays (M-F)' },
  { value: 'OTHER', label: 'Other' },
];

// ---------------------------------------------------------------------------
// Housekeeping
// ---------------------------------------------------------------------------

export const HOUSEKEEPING_TYPES_OF_SERVICES_OPTIONS: CheckboxOption[] = [
  { value: 'CLEANING', label: 'Cleaning' },
  { value: 'ERRANDS', label: 'Errands' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
];

export const HOUSEKEEPING_SERVICES_EXPERIENCE_OPTIONS: CheckboxOption[] = [
  { value: 'GENERAL_CLEANING', label: 'General Cleaning' },
  { value: 'SPECIALIZED_CLEANING', label: 'Specialized Cleaning' },
  { value: 'PREPARE_SNACKS_MEALS', label: 'Prepare Snacks/Meals' },
  { value: 'WASH_DISHES', label: 'Wash Dishes' },
  { value: 'DO_LAUNDRY', label: 'Do Laundry' },
  { value: 'RUN_ERRANDS', label: 'Run Errands' },
  { value: 'GENERAL_MAINTENANCE', label: 'General Maintenance' },
  { value: 'ORGANIZATION', label: 'Organization' },
];

// ---------------------------------------------------------------------------
// Pet care
// ---------------------------------------------------------------------------
export const PET_CARE_SERVICES_EXPERIENCE_OPTIONS: any = Object.values(PetCareExperienceOptions).map((experience) => ({
  value: experience,
  label: experience
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase()),
}));

// ---------------------------------------------------------------------------
// Companion Care
// ---------------------------------------------------------------------------

export const COMPANION_CARE_AGE_GROUP_OPTIONS: CheckboxOption[] = [
  { value: 'UNDER_55', label: 'Under 55 years old' },
  { value: 'FROM_55_TO_64', label: '55 to 64 years old' },
  { value: 'FROM_65_TO_74', label: '65 to 74 years old' },
  { value: 'FROM_75_TO_84', label: '75 to 84 years old' },
  { value: 'OVER_85', label: 'Over 85 years old' },
];

export const COMPANION_CARE_BASIC_EXPERIENCE_OPTIONS: CheckboxOption[] = [
  { value: 'CONVERSATION_COMPANIONSHIP', label: 'Conversation and companionship' },
  { value: 'SAFETY_SUPERVISION', label: 'Safety supervision' },
  { value: 'MEAL_PREPARATION', label: 'Meal preparation' },
  { value: 'MEDICATION_REMINDERS', label: 'Medication reminders' },
  { value: 'SOCIAL_ACTIVITIES', label: 'Social activities' },
  { value: 'LIGHT_HOUSEKEEPING', label: 'Light housekeeping' },
  { value: 'EXERCISE_PHYSICAL_ACTIVITIES', label: 'Exercise or physical activities' },
  { value: 'LAUNDRY_LINEN_CHANGES', label: 'Laundry and linen changes' },
  { value: 'SHOPPING', label: 'Shopping' },
  { value: 'MOBILITY_ASSISTANCE', label: 'Mobility assistance' },
];

export const COMPANION_CARE_PERSONAL_EXPERIENCE_OPTIONS: CheckboxOption[] = [
  { value: 'BATHING', label: 'Bathing' },
  { value: 'DRESSING', label: 'Dressing' },
  { value: 'GROOMING', label: 'Grooming' },
  { value: 'FEEDING_SPECIAL_DIET', label: 'Feeding and special diet' },
  { value: 'TOILETING', label: 'Toileting' },
];

export const CERTIFICATIONS_OPTIONS: CheckboxOption[] = [
  { value: 'FIRST_AID_TRAINING', label: 'First Aid' },
  { value: 'CPR_CERTIFIED', label: 'CPR' },
];

// Full specialty-skills qualifications list (matches Rails professional skills)
export const COMPANION_CARE_SPECIALTY_QUALIFICATIONS_OPTIONS: CheckboxOption[] = [
  { value: 'FIRST_AID_TRAINING', label: 'First Aid Training' },
  { value: 'CPR_CERTIFIED', label: 'CPR Training' },
  { value: 'REGISTERED_NURSE', label: 'Registered Nurse' },
  { value: 'CERTIFIED_NURSING_ASSISTANT', label: 'Certified Nursing Assistant' },
  { value: 'LICENSED_PRACTICAL_NURSE', label: 'Licensed Practical Nurse' },
  { value: 'LICENSED_VOCATIONAL_NURSE', label: 'Licensed Vocational Nurse' },
  { value: 'OCCUPATIONAL_THERAPIST', label: 'Occupational Therapist' },
  { value: 'PHYSICAL_THERAPIST', label: 'Physical Therapist' },
  { value: 'SPEECH_THERAPIST', label: 'Speech Therapist' },
  { value: 'BONDED', label: 'Bonded' },
  { value: 'LICENSED', label: 'Licensed' },
  { value: 'INSURED', label: 'Insured' },
];

export const COMPANION_CARE_INDIVIDUALIZED_SERVICES_OPTIONS: CheckboxOption[] = [
  { value: 'MEDICATION_ADMINISTRATION', label: 'Medication administration' },
  { value: 'MEDICATION_ASSISTANCE', label: 'Medication assistance' },
  { value: 'MEDICATION_MANAGEMENT', label: 'Medication management' },
  { value: 'GROOMING_GUIDANCE', label: 'Grooming guidance' },
  { value: 'ASSISTANCE_WITH_PETS', label: 'Assistance with pets' },
  { value: 'MEAL_PLANNING', label: 'Meal planning' },
  { value: 'NUTRITION_EDUCATION', label: 'Nutrition education' },
  { value: 'MOBILITY_TRAINING', label: 'Mobility training' },
];

export const COMPANION_CARE_HEALTH_SERVICES_OPTIONS: CheckboxOption[] = [
  { value: 'WOUND_CARE', label: 'Wound care' },
  { value: 'PAIN_MANAGEMENT', label: 'Pain management' },
  { value: 'IV_THERAPY_INJECTIONS', label: 'IV therapy/injections' },
  { value: 'DIABETIC_CARE', label: 'Diabetic care' },
  { value: 'CENTRAL_LINE_CARE', label: 'Central line care' },
  { value: 'OSTOMY_CARE', label: 'Ostomy care' },
];

export interface CompanionCareServiceRateOption {
  service: string;
  checkboxLabel: string;
  inputLabel: string;
  ratePath:
    | 'homeCareRates.hourly'
    | 'homeCareRates.halfDay'
    | 'homeCareRates.fullDay'
    | 'homeCareRates.weekly'
    | 'homeCareRates.monthly'
    | 'respiteCareRates.hourly'
    | 'respiteCareRates.halfDay'
    | 'respiteCareRates.fullDay'
    | 'transportationRates.oneWay';
}

export const COMPANION_CARE_SERVICE_RATE_OPTIONS: CompanionCareServiceRateOption[] = [
  {
    service: 'HOME_CARE_HOUR',
    checkboxLabel: 'Home care- hourly rate',
    inputLabel: '$ Hourly rate',
    ratePath: 'homeCareRates.hourly',
  },
  {
    service: 'HOME_CARE_HALF_DAY',
    checkboxLabel: 'Home care- half-day rate',
    inputLabel: '$ Half day rate',
    ratePath: 'homeCareRates.halfDay',
  },
  {
    service: 'HOME_CARE_DAY',
    checkboxLabel: 'Home care- full-day rate',
    inputLabel: '$ Full day rate',
    ratePath: 'homeCareRates.fullDay',
  },
  {
    service: 'HOME_CARE_WEEK',
    checkboxLabel: 'Home care- weekly rate',
    inputLabel: '$ Weekly Rate',
    ratePath: 'homeCareRates.weekly',
  },
  {
    service: 'HOME_CARE_MONTH',
    checkboxLabel: 'Home care- monthly rate',
    inputLabel: '$ Monthly Rate',
    ratePath: 'homeCareRates.monthly',
  },
  {
    service: 'RESPITE_CARE_HOUR',
    checkboxLabel: 'Respite care- hourly rate',
    inputLabel: '$ Hourly Rate',
    ratePath: 'respiteCareRates.hourly',
  },
  {
    service: 'RESPITE_CARE_HALF_DAY',
    checkboxLabel: 'Respite care- half-day rate',
    inputLabel: '$ Half day rate',
    ratePath: 'respiteCareRates.halfDay',
  },
  {
    service: 'RESPITE_CARE_DAY',
    checkboxLabel: 'Respite care- full-day rate',
    inputLabel: '$ Full day rate',
    ratePath: 'respiteCareRates.fullDay',
  },
  {
    service: 'TRANSPORT_ONE_WAY',
    checkboxLabel: 'Transportation- one-way rate',
    inputLabel: '$ Rate (one way)',
    ratePath: 'transportationRates.oneWay',
  },
];

export const numberOfPetOptions = (): SelectOption[] => [
  { value: '1', display: 'One pet' },
  { value: '2', display: 'Two pets' },
  { value: '3', display: 'Three pets' },
  { value: '4', display: 'Four or more' },
];
